// Payment Utilities for ICICI Payment Gateway
// File: src/utils/paymentUtils.js

import CryptoJS from "crypto-js";
import { getPaymentConfig } from "../config/paymentConfig";
import axiosInstance from "../services/AxiosInstance";

// Generate HMAC SHA-256 signature
export const generateHMAC = (message, keyString) => {
  try {
    const hmac = CryptoJS.HmacSHA256(message, keyString);
    return hmac.toString(CryptoJS.enc.Hex);
  } catch (error) {
    console.error("Error generating HMAC:", error);
    throw new Error("Failed to generate payment signature");
  }
};

// Generate unique transaction number
export const generateTransactionNumber = () => {
  const timestamp = Date.now();
  const random = Math.floor(Math.random() * 10000);
  return `TXN${timestamp}${random}`;
};

// Generate transaction date in required format (YYYYMMDD)
export const generateTransactionDate = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}${month}${day}`;
};

// Generate transaction time in required format (HHMMSS)
export const generateTransactionTime = () => {
  const date = new Date();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  return `${hours}${minutes}${seconds}`;
};

// Create hash text for initiate sale API
export const createInitiateSaleHashText = (paymentData) => {
  const config = getPaymentConfig();

  const hashText = [
    config.MERCHANT_ID,
    paymentData.transactionNumber,
    paymentData.amount,
    paymentData.currency,
    paymentData.transactionDate,
    paymentData.transactionTime,
    paymentData.customerEmail,
    paymentData.customerPhone,
    config.RETURN_URL,
  ].join("|");

  return hashText;
};

// Create hash text for status check API
export const createStatusCheckHashText = (transactionNumber) => {
  const config = getPaymentConfig();

  const hashText = [
    config.MERCHANT_ID,
    transactionNumber,
    generateTransactionDate(),
    generateTransactionTime(),
  ].join("|");

  return hashText;
};

// Create hash text for refund API
export const createRefundHashText = (refundData) => {
  const config = getPaymentConfig();

  const hashText = [
    config.MERCHANT_ID,
    refundData.originalTransactionNumber,
    refundData.refundTransactionNumber,
    refundData.refundAmount,
    refundData.refundDate,
    refundData.refundTime,
    refundData.reason,
  ].join("|");

  return hashText;
};

// Validate payment data
export const validatePaymentData = (paymentData) => {
  const errors = [];

  // Validate amount
  if (!paymentData.amount || paymentData.amount <= 0) {
    errors.push("Amount is required and must be greater than 0");
  }

  // Validate customer email
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (
    !paymentData.customerEmail ||
    !emailRegex.test(paymentData.customerEmail)
  ) {
    errors.push("Valid email address is required");
  }

  // Validate customer phone
  const phoneRegex = /^[0-9]{10,15}$/;
  if (
    !paymentData.customerPhone ||
    !phoneRegex.test(paymentData.customerPhone)
  ) {
    errors.push("Valid phone number is required (10-15 digits)");
  }

  // Validate customer name
  if (!paymentData.customerName || paymentData.customerName.trim().length < 2) {
    errors.push("Customer name is required (minimum 2 characters)");
  }

  return errors;
};

// Format amount to 2 decimal places
export const formatAmount = (amount) => {
  return parseFloat(amount).toFixed(2);
};

// Parse payment response
export const parsePaymentResponse = (response) => {
  try {
    if (typeof response === "string") {
      return JSON.parse(response);
    }
    return response;
  } catch (error) {
    console.error("Error parsing payment response:", error);
    throw new Error("Invalid payment response format");
  }
};

// Check if payment was successful
export const isPaymentSuccessful = (response) => {
  try {
    const parsedResponse = parsePaymentResponse(response);
    return (
      parsedResponse.responseCode === "000" &&
      parsedResponse.responseMessage === "SUCCESS"
    );
  } catch {
    return false;
  }
};

// Generate customer reference number
export const generateCustomerReference = (appointmentId, patientId) => {
  const timestamp = Date.now();
  return `REF${appointmentId}${patientId}${timestamp}`;
};

// Sanitize customer data
export const sanitizeCustomerData = (data) => {
  return {
    name: data.name ? data.name.trim() : "",
    email: data.email ? data.email.trim().toLowerCase() : "",
    phone: data.phone ? data.phone.replace(/\D/g, "") : "",
    address: data.address ? data.address.trim() : "",
  };
};

// Create payment description
export const createPaymentDescription = (appointmentData) => {
  return `Payment for appointment ${appointmentData.id} - ${appointmentData.patientName}`;
};

// Verify ICICI payment response hash
export const verifyICICIResponseHash = (responseParams, receivedHash) => {
  try {
    const config = getPaymentConfig();
    console.log("🔧 Debug - All response params:", responseParams);
    console.log("🔧 Debug - Working key:", config.WORKING_KEY);

    // Based on ICICI documentation, the hash should be HMAC_SHA256(msg, SECURE_KEY)
    // where SECURE_KEY = abc (as per documentation)

    // For response verification, we need to construct the message in the correct order
    // Let's try the most common patterns based on the ICICI documentation format

    // Pattern 1: Concatenated format (like initiateSale)
    const hashText1 = [
      responseParams.addlParam1 || "",
      responseParams.addlParam2 || "",
      responseParams.amount || "",
      responseParams.responseCode || "",
      responseParams.customerEmailID || "",
      responseParams.customerMobileNo || "",
      responseParams.merchantId || "",
      responseParams.merchantTxnNo || "",
      responseParams.paymentMode || "",
      responseParams.paymentDateTime || "",
      responseParams.txnID || "",
    ].join("");

    // Pattern 2: Essential fields only (common for response verification)
    const hashText2 = [
      responseParams.merchantTxnNo || "",
      responseParams.responseCode || "",
      responseParams.amount || "",
      responseParams.txnID || "",
    ].join("|");

    // Pattern 3: Simple concatenation (no separators)
    const hashText3 = [
      responseParams.merchantTxnNo || "",
      responseParams.responseCode || "",
      responseParams.amount || "",
      responseParams.txnID || "",
    ].join("");

    console.log("🔐 ICICI Hash text 1 (concatenated):", hashText1);
    console.log("🔐 ICICI Hash text 2 (pipe-separated):", hashText2);
    console.log("🔐 ICICI Hash text 3 (simple):", hashText3);

    // Generate expected hashes using HMAC_SHA256(msg, abc)
    const expectedHash1 = generateHMAC(hashText1, config.WORKING_KEY);
    const expectedHash2 = generateHMAC(hashText2, config.WORKING_KEY);
    const expectedHash3 = generateHMAC(hashText3, config.WORKING_KEY);

    console.log("🔐 Expected hash 1:", expectedHash1);
    console.log("🔐 Expected hash 2:", expectedHash2);
    console.log("🔐 Expected hash 3:", expectedHash3);
    console.log("🔐 Received hash:", receivedHash);

    // Check all methods
    const match1 = expectedHash1.toLowerCase() === receivedHash.toLowerCase();
    const match2 = expectedHash2.toLowerCase() === receivedHash.toLowerCase();
    const match3 = expectedHash3.toLowerCase() === receivedHash.toLowerCase();

    console.log("🔐 Hash 1 matches:", match1);
    console.log("🔐 Hash 2 matches:", match2);
    console.log("🔐 Hash 3 matches:", match3);

    return match1 || match2 || match3;
  } catch (err) {
    console.error("❌ ICICI hash verification error:", err);
    return false;
  }
};

// Debug function to test multiple hash patterns
export const debugICICIResponseHash = (responseParams, receivedHash) => {
  const config = getPaymentConfig();

  console.log("🔍 Testing ICICI hash patterns...");

  // Test various hash text constructions
  const patterns = [
    // Pattern 1: Full concatenated (like initiateSale)
    [
      responseParams.addlParam1 || "",
      responseParams.addlParam2 || "",
      responseParams.amount || "",
      responseParams.responseCode || "",
      responseParams.customerEmailID || "",
      responseParams.customerMobileNo || "",
      responseParams.merchantId || "",
      responseParams.merchantTxnNo || "",
      responseParams.paymentMode || "",
      responseParams.paymentDateTime || "",
      responseParams.txnID || "",
    ].join(""),

    // Pattern 2: Essential fields with pipe separator
    [
      responseParams.merchantTxnNo || "",
      responseParams.responseCode || "",
      responseParams.amount || "",
      responseParams.txnID || "",
    ].join("|"),

    // Pattern 3: Essential fields concatenated
    [
      responseParams.merchantTxnNo || "",
      responseParams.responseCode || "",
      responseParams.amount || "",
      responseParams.txnID || "",
    ].join(""),

    // Pattern 4: Transaction number and response code only
    `${responseParams.merchantTxnNo}${responseParams.responseCode}`,

    // Pattern 5: With response description
    [
      responseParams.merchantTxnNo || "",
      responseParams.responseCode || "",
      responseParams.responseDescription || "",
      responseParams.amount || "",
    ].join("|"),
  ];

  patterns.forEach((pattern, index) => {
    const hash = generateHMAC(pattern, config.WORKING_KEY);
    const matches = hash.toLowerCase() === receivedHash.toLowerCase();

    console.log(`Pattern ${index + 1}: "${pattern}"`);
    console.log(`Hash: ${hash}`);
    console.log(`Matches: ${matches ? "✅" : "❌"}`);
    console.log("---");

    if (matches) {
      console.log(
        `🎉 MATCH FOUND! Pattern ${index + 1} with key "${config.WORKING_KEY}"`
      );
    }
  });
};

// Update payment status to backend with specific API format
export const updatePaymentStatusToBackend = async (paymentData) => {
  try {
    const requestPayload = paymentData;
    // const requestPayload = {
    //   token: localStorage.getItem("token"),
    //   admin_id: localStorage.getItem("admin_id"),
    //   patient_id: paymentData.patient_id || paymentData.patientId,
    //   payment_status:
    //     paymentData.payment_status || paymentData.status || "success",
    //   payment_response:
    //     paymentData.payment_response || JSON.stringify(paymentData),
    //   transaction_id:
    //     paymentData.transaction_id || paymentData.transactionId || "",
    //   amount: paymentData.amount || 0,
    //   payment_type:
    //     paymentData.payment_type || paymentData.paymentType || "UPI",
    //   payment_for:
    //     paymentData.payment_for || paymentData.paymentFor || "APPOINTMENT",
    //   queue_id: paymentData.queue_id || paymentData.queueId || "",
    //   coupon_code: paymentData.coupon_code || paymentData.couponCode || "",
    //   final_amount:
    //     paymentData.final_amount ||
    //     paymentData.finalAmount ||
    //     paymentData.amount ||
    //     0,
    //   reference_id: paymentData.reference_id || paymentData.referenceId || "",
    // };

    // Filter out empty values but keep 0 values for amounts
    const filteredPayload = {};
    Object.keys(requestPayload).forEach((key) => {
      const value = requestPayload[key];
      // Keep 0 values for amounts, but skip null, undefined, or empty string for other fields
      if (
        key === "amount" || key === "final_amount"
          ? value !== null && value !== undefined
          : value !== null && value !== undefined && value !== ""
      ) {
        filteredPayload[key] = value;
      }
    });

    // Enhanced debugging for local testing
    console.log("🔍 DEBUG: Payment Status Update Request");
    console.log(
      "📤 Sending payment status update to backend:",
      filteredPayload
    );
    console.log("🌐 API Endpoint: /payment/updatePaymentStatus");
    console.log("🔑 Token:", filteredPayload.token ? "Present" : "Missing");
    console.log("👤 Admin ID:", filteredPayload.admin_id);
    console.log("🏥 Patient ID:", filteredPayload.patient_id);
    console.log("💰 Amount:", filteredPayload.amount);
    console.log("📊 Status:", filteredPayload.payment_status);

    // Store in localStorage for debugging
    const debugData = {
      timestamp: new Date().toISOString(),
      endpoint: "/payment/updatePaymentStatus",
      payload: filteredPayload,
      status: "pending",
    };
    localStorage.setItem("lastPaymentStatusUpdate", JSON.stringify(debugData));

    const response = await axiosInstance.post(
      "/payment/updatePaymentStatus",
      filteredPayload
    );

    // Update debug data with response
    debugData.status = "success";
    debugData.response = response.data;
    debugData.responseTime = new Date().toISOString();
    localStorage.setItem("lastPaymentStatusUpdate", JSON.stringify(debugData));

    console.log("📥 Backend response:", response.data);
    console.log("✅ Payment status updated successfully:", response.data);

    if (response.data?.status === "success") {
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || "Payment status updated successfully",
      };
    } else {
      throw new Error(
        response.data?.message || "Failed to update payment status"
      );
    }
  } catch (error) {
    console.error("❌ Error updating payment status:", error);

    // Update debug data with error
    const debugData = JSON.parse(
      localStorage.getItem("lastPaymentStatusUpdate") || "{}"
    );
    debugData.status = "error";
    debugData.error = error.message;
    debugData.errorTime = new Date().toISOString();
    localStorage.setItem("lastPaymentStatusUpdate", JSON.stringify(debugData));

    return {
      success: false,
      error:
        error.response?.data?.message ||
        error.message ||
        "Failed to update payment status",
    };
  }
};
